import { FormInputs } from './client.types';
import {
  readFile,
  getCroppedImg,
  createImage,
} from './ImageCropper/file-utils';

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const getIsClientFormDisabled = (
  gameBuildCreationLoading: boolean,
  gameBuildLoading: boolean,
  isReadOnly: boolean,
  isGameExpired: boolean
): boolean => {
  // console.log('🔍 Проверка isFormDisabled:', {
  //   gameBuildCreationLoading,
  //   gameBuildLoading,
  //   isReadOnly,
  //   isGameExpired
  // });
  
  const isDisabled = gameBuildCreationLoading || gameBuildLoading || isReadOnly || isGameExpired;
  
  if (isDisabled) {
    console.log('❌ Форма отключена:', {
      reason: gameBuildCreationLoading ? 'создание игры' : 
              gameBuildLoading ? 'загрузка игры' :
              isReadOnly ? 'только чтение' :
              isGameExpired ? 'игра истекла' : 'неизвестно'
    });
  } else {
    console.log('✅ Форма активна');
  }
  
  return isDisabled;
};

export const validateFormData = (formData: FormInputs): string[] => {
  const errors: string[] = [];
  
  // console.log('🔍 Валидация formData:', formData);

  if (!formData.imagePlayer || (typeof formData.imagePlayer === 'string' && formData.imagePlayer.trim() === '')) {
    console.log('❌ Отсутствует imagePlayer');
    errors.push('Необходимо загрузить изображение птички');
  } else {
    console.log('✅ imagePlayer присутствует:', formData.imagePlayer);
    console.log('✅ imagePlayer тип:', typeof formData.imagePlayer);
    if (formData.imagePlayer instanceof File) {
      console.log('✅ imagePlayer - это File:', formData.imagePlayer.name, formData.imagePlayer.size);
    }
  }

  if (formData.enableCustomMusic && !formData.customMusicFile) {
    errors.push('Необходимо загрузить музыкальный файл');
  }

  if (formData.urlButton && formData.urlButton.length > 20) {
    errors.push('Текст GameOver слишком длинный (максимум 20 символов)');
  }

  if (formData.textTitle && formData.textTitle.length > 30) {
    errors.push('Текст заголовка слишком длинный (максимум 30 символов)');
  }

  return errors;
};

export const formatFormDataForSubmission = (formData: FormInputs) => {
  return {
    ...formData,
    urlButton: formData.urlButton || 'Game Over',
    textTitle: formData.textTitle || 'Flappy Bird',
  };
};

export const cropImage = async (image: string | File, croppedPixels: Area) => {
  let imageUrl;
  let fileName;
  
  if (typeof image === 'string') {
    imageUrl = image;
    fileName = image.split('/').pop() || image;
  } else {
    imageUrl = await readFile(image);
    fileName = image.name;
  }

  const canvasImage = await createImage(imageUrl);
  return getCroppedImg(canvasImage, croppedPixels, fileName);
};

// Функция для обрезки изображения в круг для Flappy Bird
export const cropImageToCircle = async (file: File): Promise<File> => {
  const img = await createImage(await readFile(file));
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }
  
  const size = Math.min(img.width, img.height);
  canvas.width = size;
  canvas.height = size;
  
  // Создаем круглую маску
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
  ctx.closePath();
  ctx.clip();
  
  // Рисуем изображение по центру
  const offsetX = (img.width - size) / 2;
  const offsetY = (img.height - size) / 2;
  
  ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size);
  
  return new Promise<File>(async (resolve, reject) => {
    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          // Создаем файл из blob после обрезки
          const tempFile = new File([blob], file.name, { type: 'image/webp' });
          
          // Применяем сжатие в WebP
          const { compressImageToWebP } = await import('@shared/utils/image-compressor');
          const compressionResult = await compressImageToWebP(tempFile);
          
          console.log('Circle crop compression result (WebP):', {
            originalSize: compressionResult.originalSize,
            compressedSize: compressionResult.compressedSize,
            compressionRatio: compressionResult.compressionRatio.toFixed(2) + '%'
          });
          
          resolve(compressionResult.file);
        } catch (error) {
          console.error('Compression failed, using original cropped file:', error);
          // Если сжатие не удалось, возвращаем оригинальный файл в WebP
          resolve(new File([blob], file.name, { type: 'image/webp' }));
        }
      } else {
        reject(new Error('Failed to create blob'));
      }
    }, 'image/webp', 0.95);
  });
};

// Функция для обрезки URL изображения в круг для Flappy Bird
export const cropUrlImageToCircle = async (url: string, fileName: string): Promise<File> => {
  try {
    const img = await createImage(url);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    
    const size = Math.min(img.width, img.height);
    canvas.width = size;
    canvas.height = size;
    
    // Создаем круглую маску
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
    ctx.closePath();
    ctx.clip();
    
    // Рисуем изображение по центру
    const offsetX = (img.width - size) / 2;
    const offsetY = (img.height - size) / 2;
    
    ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size);
    
    return new Promise<File>(async (resolve, reject) => {
      canvas.toBlob(async (blob) => {
        if (blob) {
          try {
            // Создаем файл из blob после обрезки
            const tempFile = new File([blob], fileName, { type: 'image/webp' });
            
            // Применяем сжатие в WebP
            const { compressImageToWebP } = await import('@shared/utils/image-compressor');
            const compressionResult = await compressImageToWebP(tempFile);
            
            console.log('Circle crop URL compression result (WebP):', {
              originalSize: compressionResult.originalSize,
              compressedSize: compressionResult.compressedSize,
              compressionRatio: compressionResult.compressionRatio.toFixed(2) + '%'
            });
            
            resolve(compressionResult.file);
          } catch (error) {
            console.error('Compression failed, using original cropped file:', error);
            // Если сжатие не удалось, возвращаем оригинальный файл в WebP
            resolve(new File([blob], fileName, { type: 'image/webp' }));
          }
        } else {
          reject(new Error('Failed to create blob'));
        }
      }, 'image/webp', 0.95);
    });
  } catch (error) {
    console.error('Error processing image URL to circle:', error);
    return new File([], fileName, { type: 'image/webp' });
  }
};
